import React, { useMemo } from "react";

import { Box, keyframes } from "@mui/material";

import { WaveformStyle } from "../../types";

interface WaveformProps {
  style: WaveformStyle;
  color: string;
  height: "compact" | "medium" | "tall";
  animated?: boolean;
  barCount?: number;
  opacity?: number;
  /** Live, normalized (0..1) amplitude samples from the playing audio. */
  levels?: number[];
  /** Whether audio is currently playing (enables the live, data-driven mode). */
  playing?: boolean;
}

const WAVEFORM_HEIGHTS = {
  compact: 32,
  medium: 52,
  tall: 80,
};

function generateBarHeights(count: number, seed: number = 42): number[] {
  const heights: number[] = [];
  let s = seed;
  for (let i = 0; i < count; i += 1) {
    s = (s * 16807 + 13) % 2147483647;
    const normalized = (s % 100) / 100;
    const bell =
      0.25 + 0.75 * Math.sin((i / count) * Math.PI) * (0.4 + 0.6 * normalized);
    heights.push(Math.max(0.12, Math.min(1, bell)));
  }
  return heights;
}

function resampleLevels(levels: number[], count: number): number[] {
  const out: number[] = new Array(count);
  const len = levels.length;
  for (let i = 0; i < count; i += 1) {
    const idx = len === count ? i : Math.floor((i / count) * len);
    const v = levels[idx] ?? 0;
    out[i] = Math.max(0.08, Math.min(1, v));
  }
  return out;
}

const pulse = keyframes`
  0%, 100% { transform: scaleY(1); }
  50% { transform: scaleY(0.4); }
`;

interface SubProps {
  color: string;
  maxHeight: number;
  animated: boolean;
  opacity: number;
  heights: number[];
  dynamic: boolean;
}

const DYNAMIC_TRANSITION = "height 90ms linear";

function BarsWaveform({
  color,
  maxHeight,
  animated,
  opacity,
  heights,
  dynamic,
}: SubProps) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "2px",
        height: maxHeight,
        width: "100%",
        opacity,
      }}
    >
      {heights.map((h, i) => (
        <Box
          // eslint-disable-next-line react/no-array-index-key
          key={i}
          sx={{
            width: `${Math.max(100 / heights.length - 1, 2)}%`,
            maxWidth: 4,
            minWidth: 1.5,
            height: `${h * 100}%`,
            backgroundColor: color,
            borderRadius: "1px",
            transformOrigin: "center",
            transition: dynamic ? DYNAMIC_TRANSITION : undefined,
            animation:
              animated && !dynamic
                ? `${pulse} ${0.8 + (i % 5) * 0.15}s ease-in-out ${(i % 7) * 0.1}s infinite`
                : "none",
          }}
        />
      ))}
    </Box>
  );
}

function MirroredBarsWaveform({
  color,
  maxHeight,
  animated,
  opacity,
  heights,
  dynamic,
}: SubProps) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "2px",
        height: maxHeight,
        width: "100%",
        opacity,
      }}
    >
      {heights.map((h, i) => (
        <Box
          // eslint-disable-next-line react/no-array-index-key
          key={i}
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "1px",
            width: `${Math.max(100 / heights.length - 1, 2)}%`,
            maxWidth: 4,
            minWidth: 1.5,
            height: "100%",
            justifyContent: "center",
          }}
        >
          <Box
            // eslint-disable-next-line react/no-array-index-key
            key={i}
            sx={{
              width: "100%",
              height: `${h * 45}%`,
              backgroundColor: color,
              borderRadius: "1px",
              transformOrigin: "bottom",
              transition: dynamic ? DYNAMIC_TRANSITION : undefined,
              animation:
                animated && !dynamic
                  ? `${pulse} ${0.8 + (i % 5) * 0.15}s ease-in-out ${(i % 7) * 0.1}s infinite`
                  : "none",
            }}
          />
          <Box
            sx={{
              width: "100%",
              height: `${h * 45}%`,
              backgroundColor: color,
              borderRadius: "1px",
              transformOrigin: "top",
              opacity: 0.6,
              transition: dynamic ? DYNAMIC_TRANSITION : undefined,
              animation:
                animated && !dynamic
                  ? `${pulse} ${0.8 + (i % 5) * 0.15}s ease-in-out ${(i % 7) * 0.1}s infinite`
                  : "none",
            }}
          />
        </Box>
      ))}
    </Box>
  );
}

function DotsWaveform({
  color,
  maxHeight,
  animated,
  opacity,
  heights,
  dynamic,
}: SubProps) {
  const dotBounce = keyframes`
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-${maxHeight * 0.3}px); }
  `;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "3px",
        height: maxHeight,
        width: "100%",
        opacity,
      }}
    >
      {heights.map((h, i) => {
        const scale = dynamic ? 0.3 + 0.7 * h : 1;
        const dotSize = (3 + h * 4) * scale;
        return (
          <Box
            // eslint-disable-next-line react/no-array-index-key
            key={i}
            sx={{
              width: dotSize,
              height: dotSize,
              borderRadius: "50%",
              backgroundColor: color,
              transition: dynamic
                ? "width 90ms linear, height 90ms linear"
                : undefined,
              animation:
                animated && !dynamic
                  ? `${dotBounce} ${1 + (i % 4) * 0.2}s ease-in-out ${(i % 6) * 0.12}s infinite`
                  : "none",
            }}
          />
        );
      })}
    </Box>
  );
}

function LineWaveform({
  color,
  maxHeight,
  animated,
  opacity,
  heights,
  dynamic,
}: SubProps) {
  const points = heights
    .map((h, i) => {
      const x = (i / (heights.length - 1)) * 100;
      const y = 50 - h * 40;
      return `${x},${y}`;
    })
    .join(" ");

  const draw = keyframes`
    0% { stroke-dashoffset: 1000; }
    100% { stroke-dashoffset: 0; }
  `;

  return (
    <Box
      sx={{
        height: maxHeight,
        width: "100%",
        opacity,
        display: "flex",
        alignItems: "center",
      }}
    >
      <svg
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
        style={{ width: "100%", height: "100%" }}
      >
        <polyline
          points={points}
          fill="none"
          stroke={color}
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          vectorEffect="non-scaling-stroke"
          style={
            animated && !dynamic
              ? {
                  strokeDasharray: 1000,
                  animation: `${draw} 3s ease-in-out infinite alternate`,
                }
              : { transition: "all 0.06s linear" }
          }
        />
      </svg>
    </Box>
  );
}

export default function Waveform({
  style,
  color,
  height,
  animated = true,
  barCount = 48,
  opacity = 1,
  levels,
  playing = false,
}: WaveformProps) {
  const maxHeight = WAVEFORM_HEIGHTS[height];

  const staticHeights = useMemo(() => generateBarHeights(barCount), [barCount]);

  const dynamic = Boolean(playing && levels && levels.length > 0);
  const heights =
    dynamic && levels ? resampleLevels(levels, barCount) : staticHeights;

  const props: SubProps = {
    color,
    maxHeight,
    animated,
    opacity,
    heights,
    dynamic,
  };

  switch (style) {
    case "mirrored":
      return <MirroredBarsWaveform {...props} />;
    case "dots":
      return <DotsWaveform {...props} />;
    case "line":
      return <LineWaveform {...props} />;
    case "bars":
    default:
      return <BarsWaveform {...props} />;
  }
}
